import React, { useEffect, useRef, useState } from "react";
import { View, StyleSheet, ActivityIndicator, Alert } from "react-native";

import { formatDateWithTime } from "../../../../utils/date";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import QuestionCard from "./QuestionCard";
import {
  useGetAssessmentSubmissionsDetailsQuery,
  UpdateAssessmentSubmissionUpdatedGrade,
  UpdatedEntityType,
  UnifiedSubmission,
} from "../../../../graphql/generated/graphql";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";

export interface GradeChangeParams {
  updatedEntityType: UpdatedEntityType;
  updatedEntityId: string;
  updatedGrade: string;
  maxPointsForEntity: number;
}

export interface GradeTree {
  id: string;
  totalPoints: string;
  maximumPoints: number;
  questions: {
    id: string;
    totalPoints: string;
    maximumPoints: number;
    rubrics: {
      id: string;
      pointsAwarded: string;
      maxPoints: number;
    }[];
  }[];
}

interface SubmissionGradingProps {
  handleSaveAssessmentGrade: (
    submissionData: UnifiedSubmission,
    updatedGradeEntry: UpdateAssessmentSubmissionUpdatedGrade,
    closeModal: boolean
  ) => void;
}

const SubmissionGrading: React.FC<SubmissionGradingProps> = ({ handleSaveAssessmentGrade }) => {
  const { selectedSubmissionData, setUpdatedAssessmentGradeEntry, updatedAssessmentGradeEntry } =
    useGradingCenterSubmissions();

  const [gradeTree, setGradeTree] = useState<GradeTree | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  const lastSavedState = useRef<string>("");

  const [activelyEditedGradeElement, setActivelyEditedGradeElement] = useState<string>("");

  const { data: assessmentSubmissionDetails, loading: assessmentSubmissionDetailsLoading } =
    useGetAssessmentSubmissionsDetailsQuery({
      variables: {
        assessmentSubmissionId: selectedSubmissionData.id,
        studentId: selectedSubmissionData.student?.id,
      },
      skip: !selectedSubmissionData?.id || !selectedSubmissionData?.student?.id,
    });

  const detailedAssessmentSubmission = assessmentSubmissionDetails?.getAssessmentSubmissionDetails?.data;

  useEffect(() => {
    if (!isSaving && detailedAssessmentSubmission) {
      setGradeTree({
        id: detailedAssessmentSubmission?.id,
        totalPoints: detailedAssessmentSubmission?.totalPoints?.toString() ?? "0",
        maximumPoints: detailedAssessmentSubmission?.maximumPoints ?? 0,
        questions: detailedAssessmentSubmission?.questions?.map((question) => ({
          id: question.id,
          totalPoints: question.userAnswer.totalPoints?.toString() ?? "0",
          maximumPoints: question.userAnswer.maximumPoints ?? 0,
          rubrics: question.rubrics.map((rubric) => ({
            id: rubric.id,
            pointsAwarded: rubric.pointsAwarded?.toString() ?? "0",
            maxPoints: rubric.maxPoints ?? 0,
          })),
        })),
      });
    }
  }, [detailedAssessmentSubmission, isSaving]);

  useEffect(() => {
    if (detailedAssessmentSubmission) {
      setUpdatedAssessmentGradeEntry({
        updatedEntityType: null,
        updatedEntityId: null,
        updatedGrade: "",
        updatedFeedback: detailedAssessmentSubmission.feedback,
      });
    }
  }, [assessmentSubmissionDetails]);

  // Calculate time taken
  const getTimeTaken = () => {
    if (detailedAssessmentSubmission?.startedAt && detailedAssessmentSubmission?.completedAt) {
      const start = new Date(detailedAssessmentSubmission.startedAt);
      const end = new Date(detailedAssessmentSubmission.completedAt);
      const diffMs = end.getTime() - start.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins} min`;
    }
    return "0 min";
  };

  const handleGradeChange = async (input: GradeChangeParams) => {
    const { updatedEntityType, updatedEntityId, updatedGrade, maxPointsForEntity } = input;

    if (isSaving || !updatedGrade || updatedGrade.trim() === "") {
      return;
    }

    // trim all non-numeric characters
    let sanitizedUpdatedGrade = updatedGrade.replace(",", ".");
    sanitizedUpdatedGrade = sanitizedUpdatedGrade.replace(/[^0-9.]/g, "");

    const numberUpdatedGrade = sanitizedUpdatedGrade;

    if (Number.isNaN(Number(numberUpdatedGrade))) {
      Alert.alert("Invalid score", "Please enter a valid number");
      return;
    }

    const clampedGrade = Math.min(Math.max(Number(numberUpdatedGrade), 0), maxPointsForEntity).toString();

    if (updatedEntityType === UpdatedEntityType.Submission) {
      setGradeTree({
        ...gradeTree,
        totalPoints: clampedGrade,
      });
    } else if (updatedEntityType === UpdatedEntityType.Question) {
      setGradeTree({
        ...gradeTree,
        questions: gradeTree.questions.map((question) =>
          question.id === updatedEntityId ? { ...question, totalPoints: clampedGrade } : question
        ),
      });
    } else if (updatedEntityType === UpdatedEntityType.Rubric) {
      const q = gradeTree.questions.find((q) => {
        return q.rubrics.some((r) => r.id === updatedEntityId);
      });

      setGradeTree({
        ...gradeTree,
        questions: gradeTree.questions.map((question) => {
          if (question.id === q.id) {
            return {
              ...question,
              rubrics: question.rubrics.map((r) =>
                r.id === updatedEntityId ? { ...r, pointsAwarded: clampedGrade } : r
              ),
            };
          }

          return question;
        }),
      });
    }

    setUpdatedAssessmentGradeEntry({
      updatedEntityType,
      updatedEntityId,
      updatedGrade: clampedGrade,
    });
  };

  const handleSave = async (modalActionPressed: boolean) => {
    setActivelyEditedGradeElement("");

    if (isSaving || !detailedAssessmentSubmission) {
      return;
    }

    const currentStateId = `${updatedAssessmentGradeEntry.updatedEntityType}-${updatedAssessmentGradeEntry.updatedEntityId}-${updatedAssessmentGradeEntry.updatedGrade}`;

    if (currentStateId === lastSavedState.current) {
      return;
    }

    try {
      setIsSaving(true);
      lastSavedState.current = currentStateId;

      await handleSaveAssessmentGrade(selectedSubmissionData, updatedAssessmentGradeEntry, modalActionPressed);
    } catch (error) {
      console.error("Save failed:", error);
      lastSavedState.current = "";
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <View style={styles.assessmentTabContent}>
      {/* Student Info */}
      <View style={styles.studentAssessmentInfo}>
        <View style={styles.studentDetails}>
          <View style={styles.studentAvatar}>
            <CustomText
              text={selectedSubmissionData?.student?.initials}
              size="s"
              weight="bold"
              style={styles.avatarText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.studentInfo}>
            <CustomText
              text={selectedSubmissionData?.student?.name}
              size="s"
              weight="bold"
              useTranslationText={false}
            />
            <CustomText
              text={selectedSubmissionData?.student?.email}
              size="xs"
              style={styles.email}
              useTranslationText={false}
            />
          </View>
        </View>
        <View style={styles.assessmentDetails}>
          <View style={styles.assessmentDetail}>
            <CustomText text="submitted" size="xs" style={styles.assessmentDetailText} />
            <CustomText
              text={formatDateWithTime(detailedAssessmentSubmission?.created || "")}
              size="xs"
              weight="bold"
              useTranslationText={false}
            />
          </View>
          <View style={styles.assessmentDetail}>
            <CustomText text="time_spent" size="xs" style={styles.assessmentDetailText} />
            <CustomText text={getTimeTaken()} size="xs" weight="bold" useTranslationText={false} />
            <CustomText
              text="out_of"
              size="xs"
              interpolation={{ timeLimit: detailedAssessmentSubmission?.timeLimit }}
              style={[styles.assessmentDetailText, styles.assessmentDetailLimit]}
            />
          </View>
        </View>
      </View>

      {/* Assessment Score Section */}
      <View style={styles.assessmentScoreSection}>
        <CustomText text="Assessment Score" size="l" weight="bold" style={styles.sectionTitle} />

        <View style={styles.scoreSummary}>
          <View style={styles.aiScoreDisplay}>
            <CustomText text="AI-Generated Score" weight="normal" style={styles.scoreLabel} />
            <CustomText
              text={`${gradeTree?.totalPoints}/${gradeTree?.maximumPoints}`}
              size="xxl"
              weight="bold"
              style={styles.scoreValue}
              useTranslationText={false}
            />
          </View>

          <View style={styles.overrideScoreContainer}>
            <CustomText text="Final Score (Override)" size="s" style={styles.label} />
            <View style={styles.scoreInputWrapper}>
              <CustomTextInput
                onFocus={() => {
                  setActivelyEditedGradeElement(gradeTree?.id);
                }}
                value={gradeTree?.totalPoints}
                onChangeText={(text) =>
                  handleGradeChange({
                    updatedEntityType: UpdatedEntityType.Submission,
                    updatedEntityId: gradeTree.id,
                    updatedGrade: text,
                    maxPointsForEntity: gradeTree.maximumPoints,
                  })
                }
                placeholder=""
                keyboardType="numeric"
                style={styles.scoreInput}
                onBlur={() => handleSave(false)}
              />
              <CustomText text={`/${gradeTree?.maximumPoints}`} weight="bold" useTranslationText={false} />
            </View>
            <CustomText
              text="Leave blank to use AI score, or enter a value to override the current score."
              size="xs"
              style={styles.scoreHelp}
            />
          </View>
        </View>

        {/* <CustomButton
          text="Allow Retake"
          leftIcon={<ArrowClockwise size={14} color="#3E68FE" />}
          styleType="primaryTransparentStroke"
          onPress={() => {}}
        />
        */}
      </View>

      {/* Question Breakdown Section */}
      <View style={styles.questionBreakdownSection}>
        <CustomText text="Question Breakdown" size="l" weight="bold" style={styles.sectionTitle} />
        {assessmentSubmissionDetailsLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#3E68FE" />
          </View>
        ) : (
          <View style={styles.questionsList}>
            {detailedAssessmentSubmission?.questions?.map((question, index) => {
              const gradeTreeQuestion = gradeTree?.questions?.find((q) => q.id === question.id);

              return (
                <QuestionCard
                  key={question.id}
                  question={question}
                  gradeTreeQuestion={gradeTreeQuestion}
                  questionNumber={`Question ${index + 1}`}
                  setActivelyEditedGradeElement={setActivelyEditedGradeElement}
                  activelyEditedGradeElement={activelyEditedGradeElement}
                  handleGradeChange={handleGradeChange}
                  handleSave={handleSave}
                />
              );
            })}
          </View>
        )}
      </View>

      {/* Overall Feedback Section */}
      <View style={styles.feedbackContainer}>
        <CustomText text="Overall Feedback (Optional)" size="s" style={styles.label} />
        <CustomTextInput
          value={updatedAssessmentGradeEntry?.updatedFeedback}
          onChangeText={(text) =>
            setUpdatedAssessmentGradeEntry({
              ...updatedAssessmentGradeEntry,
              updatedEntityType: UpdatedEntityType.Submission,
              updatedEntityId: gradeTree?.id,
              updatedGrade: gradeTree?.totalPoints,
              updatedFeedback: text,
            })
          }
          placeholder="Provide overall feedback on the student's assessment performance..."
          multiline
          numberOfLines={4}
          maxLength={500}
          style={styles.feedbackTextarea}
        />
        <CustomText
          text="character_count"
          size="xs"
          interpolation={{ value: updatedAssessmentGradeEntry?.updatedFeedback?.length || 0 }}
          style={styles.feedbackCharCount}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  assessmentTabContent: {
    display: "flex",
  },
  sectionTitle: {
    color: "#374151",
    marginBottom: 12,
  },
  label: {
    marginBottom: 8,
    color: "#344054",
  },
  // Student Assessment Info Styles
  studentAssessmentInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: "#BFDBFE",
    borderRadius: 10,
    padding: 16,
    marginBottom: 24,
  },
  studentDetails: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  studentAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "#3B82F6",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#FFFFFF",
  },
  studentInfo: {
    flex: 1,
  },
  email: {
    color: "#6B7280",
    marginTop: 2,
  },
  assessmentDetails: {
    alignItems: "flex-end",
  },
  assessmentDetail: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  assessmentDetailText: {
    color: "#475467",
  },
  assessmentDetailLimit: {
    fontStyle: "italic",
  },
  // Assessment Score Section Styles
  assessmentScoreSection: {
    marginBottom: 24,
    alignItems: "flex-start",
  },
  scoreSummary: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 24,
    marginBottom: 20,
  },
  aiScoreDisplay: {
    maxWidth: 300,
    padding: 16,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    backgroundColor: "#F0FDF4",
    borderWidth: 1,
    borderColor: "#BBF7D0",
    borderRadius: 8,
  },
  scoreLabel: {
    color: "#344054",
  },
  scoreValue: {
    color: "#3E68FE",
  },
  overrideScoreContainer: {
    flex: 1,
  },
  scoreInputWrapper: {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    maxWidth: 110,
  },
  scoreInput: {
    padding: 12,
    paddingRight: 10,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    fontSize: 16,
    fontWeight: "600",
  },
  scoreHelp: {
    color: "#6B7280",
    marginTop: 6,
  },
  // Question Breakdown Section Styles
  questionBreakdownSection: {
    marginBottom: 24,
  },
  questionsList: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  // Feedback Section Styles
  feedbackContainer: {
    flex: 1,
  },
  feedbackTextarea: {
    marginTop: 8,
    minHeight: 100,
    textAlignVertical: "top",
  },
  feedbackCharCount: {
    color: "#6B7280",
    textAlign: "right",
    marginTop: 6,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
});

export default SubmissionGrading;
